import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../../components/ManagementHeader";
import Footer from "../../../components/Footer";
import { supabase } from "../../../supabaseClient";
import {
  MAINTENANCE_OWNER_EMAIL,
  formatMaintenanceMessage,
  isMaintenanceOwner,
} from "../../../utils/maintenance";

const DEFAULT_MESSAGE =
  "This service is currently undergoing planned maintenance. Please check back shortly.";

function formatDateTime(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ManagementMaintenance({ session, profile }) {
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const owner = useMemo(() => isMaintenanceOwner(session), [session]);

  const loadSetting = useCallback(async () => {
    setLoading(true);
    setFeedback(null);

    const { data, error } = await supabase
      .from("site_maintenance")
      .select("enabled, message, updated_at")
      .eq("id", 1)
      .maybeSingle();

    if (error) {
      console.error("Unable to load maintenance setting:", error);
      setFeedback({
        type: "error",
        text: "The maintenance setting could not be loaded. Run ADD_SITE_MAINTENANCE.sql in Supabase first.",
      });
      setLoading(false);
      return;
    }

    setEnabled(data?.enabled === true);
    setMessage(formatMaintenanceMessage(data?.message));
    setUpdatedAt(data?.updated_at || null);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadSetting();
  }, [loadSetting]);

  async function saveMaintenance(nextEnabled) {
    if (!owner || saving) return;

    if (
      nextEnabled &&
      !window.confirm(
        "Enable site-wide maintenance? Staff, visitors, patients and public users will be locked out. Management accounts will remain able to sign in and bypass maintenance.",
      )
    ) {
      return;
    }

    setSaving(true);
    setFeedback(null);

    const nextMessage = formatMaintenanceMessage(message);
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from("site_maintenance")
      .upsert(
        {
          id: 1,
          enabled: nextEnabled,
          message: nextMessage,
          updated_at: now,
          updated_by: session?.user?.id || null,
        },
        { onConflict: "id" },
      )
      .select("enabled, message, updated_at")
      .single();

    if (error) {
      console.error("Unable to save maintenance setting:", error);
      setFeedback({
        type: "error",
        text:
          error.message ||
          "The maintenance setting could not be saved. Only the authorised maintenance account can change it.",
      });
      setSaving(false);
      return;
    }

    setEnabled(data.enabled === true);
    setMessage(formatMaintenanceMessage(data.message));
    setUpdatedAt(data.updated_at || now);
    setFeedback({
      type: "success",
      text: data.enabled
        ? "Maintenance mode is ON. Management accounts remain exempt; other users are locked out."
        : "Maintenance mode is OFF. The site is available normally again.",
    });

    window.dispatchEvent(
      new CustomEvent("ruht:maintenance-changed", {
        detail: {
          enabled: data.enabled === true,
          message: formatMaintenanceMessage(data.message),
          updated_at: data.updated_at || now,
        },
      }),
    );

    setSaving(false);
  }

  async function saveMessage() {
    if (!owner || saving) return;
    setSaving(true);
    setFeedback(null);

    const nextMessage = formatMaintenanceMessage(message);
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from("site_maintenance")
      .update({
        message: nextMessage,
        updated_at: now,
        updated_by: session?.user?.id || null,
      })
      .eq("id", 1)
      .select("enabled, message, updated_at")
      .single();

    if (error) {
      console.error("Unable to save maintenance message:", error);
      setFeedback({
        type: "error",
        text: error.message || "The maintenance message could not be saved.",
      });
    } else {
      setMessage(formatMaintenanceMessage(data.message));
      setUpdatedAt(data.updated_at || now);
      setFeedback({ type: "success", text: "Maintenance message saved." });
      window.dispatchEvent(
        new CustomEvent("ruht:maintenance-changed", {
          detail: {
            enabled: data.enabled === true,
            message: formatMaintenanceMessage(data.message),
            updated_at: data.updated_at || now,
          },
        }),
      );
    }

    setSaving(false);
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header session={session} profile={profile} />

      <main id="main-content" className="nhsuk-width-container pguh-maintenance-admin">
        <div className="pguh-maintenance-admin__heading">
          <div>
            <span className="pguh-eyebrow">Management control</span>
            <h1 className="nhsuk-heading-l">Maintenance</h1>
            <p className="nhsuk-body-l">
              Lock the entire RUHT website while planned work is taking place.
            </p>
          </div>

          <div
            className={`pguh-maintenance-status ${
              enabled ? "pguh-maintenance-status--on" : "pguh-maintenance-status--off"
            }`}
          >
            <span className="pguh-maintenance-status__dot" aria-hidden="true" />
            <span>
              <strong>{enabled ? "Maintenance ON" : "Site online"}</strong>
              <small>Updated {formatDateTime(updatedAt)}</small>
            </span>
          </div>
        </div>

        {feedback && (
          <div
            className={`pguh-timesheet-message pguh-timesheet-message--${feedback.type}`}
            role={feedback.type === "error" ? "alert" : "status"}
          >
            {feedback.text}
          </div>
        )}

        <div className="nhsuk-warning-callout">
          <h2 className="nhsuk-warning-callout__label">
            <span role="text">
              <span className="nhsuk-u-visually-hidden">Important: </span>
              Owner-only control
            </span>
          </h2>
          <p>
            This page and its database write permissions are restricted to <strong>{MAINTENANCE_OWNER_EMAIL}</strong>.
            This page remains restricted to the maintenance owner for changing the maintenance setting. While maintenance mode is enabled, all Management accounts are exempt from the site lock and can continue using the intranet.
          </p>
        </div>

        <section className="pguh-maintenance-control-card" aria-labelledby="maintenance-switch-heading">
          <div>
            <span className="pguh-eyebrow">Site availability</span>
            <h2 id="maintenance-switch-heading" className="nhsuk-heading-m">
              Site-wide maintenance mode
            </h2>
            <p className="nhsuk-body-m">
              Enabling this replaces the public site, patient portal and non-management staff pages with the maintenance screen. Management accounts continue to have access.
            </p>
          </div>

          <button
            type="button"
            className={`pguh-maintenance-toggle ${enabled ? "pguh-maintenance-toggle--on" : ""}`}
            role="switch"
            aria-checked={enabled}
            aria-label="Site-wide maintenance mode"
            disabled={loading || saving || !owner}
            onClick={() => saveMaintenance(!enabled)}
          >
            <span className="pguh-maintenance-toggle__track">
              <span className="pguh-maintenance-toggle__thumb" />
            </span>
            <span>{saving ? "Saving…" : enabled ? "On" : "Off"}</span>
          </button>
        </section>

        <section className="pguh-maintenance-message-card" aria-labelledby="maintenance-message-heading">
          <h2 id="maintenance-message-heading" className="nhsuk-heading-m">
            Public maintenance message
          </h2>
          <p className="nhsuk-body-m">
            This is the message everyone else sees while the site is locked.
          </p>

          <div className="nhsuk-form-group">
            <label className="nhsuk-label" htmlFor="maintenance-message">
              Message
            </label>
            <textarea
              id="maintenance-message"
              className="nhsuk-textarea"
              rows="5"
              maxLength="1000"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              disabled={loading || saving || !owner}
            />
            <div className="nhsuk-hint">{message.length}/1000 characters</div>
          </div>

          <button
            type="button"
            className="nhsuk-button nhsuk-button--secondary"
            onClick={saveMessage}
            disabled={loading || saving || !owner}
          >
            {saving ? "Saving…" : "Save maintenance message"}
          </button>
        </section>

        <section className="pguh-maintenance-preview" aria-labelledby="maintenance-preview-heading">
          <span className="pguh-eyebrow">Preview</span>
          <h2 id="maintenance-preview-heading" className="nhsuk-heading-m">
            What other users will see
          </h2>
          <div className="pguh-maintenance-preview__screen">
            <strong>Service temporarily unavailable</strong>
            <p>{formatMaintenanceMessage(message)}</p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
